import { useState, useEffect } from 'react'
import { Plus, Search, Eye, Edit, Trash2, X, CheckCircle, AlertCircle, Loader2, Wand2, Building, ChevronLeft, ChevronRight, UserX, RefreshCw } from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { Select } from '../components/ui/Select'
import { partnerAdminApi } from '../services/api'
import { useCNPJValidation, formatCNPJ, cleanCNPJ, validateCNPJChecksum } from '../hooks/useCNPJValidation'
import { ClientOnboardingWizard } from '../components/ClientOnboardingWizard'

interface Client {
  id: string
  name: string
  email: string
  cpf?: string
  cnpj?: string
  speciality: string
  plan: 'basic' | 'pro' | 'enterprise'
  status: 'active' | 'suspended' | 'trial' | 'cancelled'
  revenue: number
  lastAccess: string
  documentsCount: number
}

interface ApiClient {
  id: string
  name: string
  slug: string
  status: string
  subscriptionPlan: string
  monthlyRevenue: number
  lastActivity: string
  joinedDate: string
  healthScore: number
  pendingTasks: number
  totalUsers: number
  location: string
  contact: {
    email: string
    phone: string
  }
}

interface ApiResponse {
  clients: ApiClient[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

/**
 * Client Management Page for Partner Admin
 *
 * Purpose: Allow accounting offices to manage their medical practice clients
 * Features:
 * - View only clients assigned to this partner
 * - CRUD operations on clients
 * - Monitor client activity and revenue
 * - Filter and search capabilities
 *
 * Data Isolation: Only shows clients belonging to current partner
 */
export function ClientManagement() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterPlan, setFilterPlan] = useState<string>('all')
  const [filterAccountant, setFilterAccountant] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [createdCredentials, setCreatedCredentials] = useState<{ email: string; password: string; clinicName: string } | null>(null)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [deletingClient, setDeletingClient] = useState<Client | null>(null)
  const [cancellingClient, setCancellingClient] = useState<Client | null>(null)
  const [cancellationReason, setCancellationReason] = useState<string>('')
  const [cancellationDetails, setCancellationDetails] = useState<string>('')
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    cnpj: '',
    speciality: '',
    subscriptionPlan: 'basic',
    accountantId: ''
  })
  const [accountants, setAccountants] = useState<{ id: string; name: string; email: string }[]>([])
  const [clientAssignments, setClientAssignments] = useState<Map<string, string>>(new Map()) // clientId -> accountantId
  const [editClient, setEditClient] = useState({
    name: '',
    email: '',
    phone: '',
    cnpj: '',
    speciality: '',
    subscriptionPlan: 'basic',
    status: 'active' as 'active' | 'suspended' | 'trial',
    accountantId: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // CNPJ Validation hook
  const cnpjValidation = useCNPJValidation()
  const [cnpjStatus, setCnpjStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid' | 'exists'>('idle')

  // Fetch clients from API
  const fetchClients = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: { search?: string; status?: string } = {}
      if (searchTerm) params.search = searchTerm
      if (filterStatus !== 'all') params.status = filterStatus

      const data = await partnerAdminApi.clients.list(params)
      console.log('Clients API response:', data)

      // Map API response to local client format
      const mappedClients: Client[] = data.clients.map(apiClient => ({
        id: apiClient.id,
        name: apiClient.name,
        email: apiClient.contact.email,
        speciality: apiClient.location || 'Geral',
        plan: mapPlanToLocal(apiClient.subscriptionPlan),
        status: mapStatusToLocal(apiClient.status),
        revenue: apiClient.monthlyRevenue,
        lastAccess: apiClient.lastActivity,
        documentsCount: apiClient.pendingTasks
      }))

      setClients(mappedClients)
      setLoading(false)

    } catch (error) {
      console.error('Error fetching clients:', error)
      setError('Erro ao carregar clientes')
      setClients([])
      setLoading(false)
    }
  }

  // Helper functions to map API values to local types
  const mapPlanToLocal = (plan: string): 'basic' | 'pro' | 'enterprise' => {
    const planMap: Record<string, 'basic' | 'pro' | 'enterprise'> = {
      'Básico': 'basic',
      'Profissional': 'pro',
      'Premium': 'enterprise',
      'Enterprise': 'enterprise'
    }
    return planMap[plan] || 'basic'
  }

  const mapStatusToLocal = (status: string): 'active' | 'suspended' | 'trial' => {
    const statusMap: Record<string, 'active' | 'suspended' | 'trial'> = {
      'active': 'active',
      'suspended': 'suspended',
      'trial': 'trial',
      'pending': 'trial'
    }
    return statusMap[status] || 'active'
  }

  // Handle CNPJ input change with formatting and validation
  const handleCnpjChange = async (value: string) => {
    const cleaned = cleanCNPJ(value)
    const formatted = cleaned.length <= 14 ? formatCNPJ(cleaned) : formatCNPJ(cleaned.slice(0, 14))

    setNewClient({ ...newClient, cnpj: formatted })
    setCnpjStatus('idle')
    cnpjValidation.reset()

    // Only validate when we have a complete CNPJ
    if (cleaned.length === 14) {
      // First, quick local validation
      if (!validateCNPJChecksum(cleaned)) {
        setCnpjStatus('invalid')
        return
      }

      setCnpjStatus('validating')

      // Check if CNPJ already exists in partner's clients
      const existsResult = await cnpjValidation.checkExists(cleaned)
      if (existsResult?.exists) {
        setCnpjStatus('exists')
        return
      }

      // Full validation with Receita Federal lookup
      const fullResult = await cnpjValidation.validateFull(cleaned)
      if (fullResult) {
        if (fullResult.valid) {
          setCnpjStatus('valid')
          // Auto-fill company name if available
          if (fullResult.rfData?.razaoSocial && !newClient.name) {
            setNewClient(prev => ({
              ...prev,
              name: fullResult.rfData?.nomeFantasia || fullResult.rfData?.razaoSocial || prev.name
            }))
          }
        } else {
          setCnpjStatus('invalid')
        }
      } else {
        setCnpjStatus('invalid')
      }
    }
  }

  // Add new client
  const handleAddClient = async () => {
    try {
      setSubmitting(true)
      setError(null)

      // Validate CNPJ if provided
      if (newClient.cnpj && cnpjStatus !== 'valid') {
        setError('Por favor, informe um CNPJ válido')
        setSubmitting(false)
        return
      }

      const { accountantId, ...clientFields } = newClient
      const clientData = {
        ...clientFields,
        cnpj: newClient.cnpj ? cleanCNPJ(newClient.cnpj) : undefined
      }

      const result = await partnerAdminApi.clients.create(clientData)
      console.log('Client created:', result)

      // If accountant was selected, assign the client to the accountant
      if (accountantId && result.client?.id) {
        try {
          await partnerAdminApi.clients.assign(result.client.id, accountantId)
          console.log('Client assigned to accountant:', accountantId)
        } catch (assignError) {
          console.error('Error assigning client to accountant:', assignError)
          // Don't fail the whole operation if assignment fails
        }
      }

      // Store credentials to show in success modal
      if (result.adminUser?.temporaryPassword) {
        setCreatedCredentials({
          email: result.adminUser.email,
          password: result.adminUser.temporaryPassword,
          clinicName: result.client?.name || clientData.name
        })
        setShowSuccessModal(true)
      }

      // Reset form and close modal
      setNewClient({ name: '', email: '', phone: '', cnpj: '', speciality: '', subscriptionPlan: 'basic', accountantId: '' })
      setCnpjStatus('idle')
      cnpjValidation.reset()
      setShowAddModal(false)

      // Refresh clients list
      fetchClients()

    } catch (error: any) {
      console.error('Error adding client:', error)
      setError(error.response?.data?.message || error.message || 'Erro ao cadastrar clínica')
    } finally {
      setSubmitting(false)
    }
  }

  // Open edit modal with client data
  const handleOpenEditModal = async (client: Client) => {
    setEditingClient(client)
    setEditClient({
      name: client.name,
      email: client.email,
      phone: '',
      cnpj: client.cnpj || '',
      speciality: client.speciality,
      subscriptionPlan: client.plan,
      status: client.status,
      accountantId: ''
    })
    setError(null)
    setShowEditModal(true)

    // Load current accountant assignment for this client
    try {
      const assignments = await partnerAdminApi.accountants.getAllAssignments()
      const clientAssignment = assignments.assignments.find(
        (a: any) => a.client.id === client.id || a.clientId === client.id
      )
      if (clientAssignment) {
        setEditClient(prev => ({
          ...prev,
          accountantId: clientAssignment.accountant?.id || clientAssignment.accountantId || ''
        }))
      }
    } catch (err) {
      console.error('Error loading client accountant:', err)
    }
  }

  // Update existing client
  const handleUpdateClient = async () => {
    if (!editingClient) return

    try {
      setSubmitting(true)
      setError(null)

      const updateData = {
        name: editClient.name,
        email: editClient.email,
        phone: editClient.phone || undefined,
        speciality: editClient.speciality,
        subscriptionPlan: editClient.subscriptionPlan,
        subscription_status: editClient.status === 'active' ? 'active' :
                            editClient.status === 'suspended' ? 'suspended' : 'trial'
      }

      await partnerAdminApi.clients.update(editingClient.id, updateData)

      // Handle accountant assignment change
      // First, get current assignment
      const assignments = await partnerAdminApi.accountants.getAllAssignments()
      const currentAssignment = assignments.assignments.find(
        (a: any) => a.client.id === editingClient.id || a.clientId === editingClient.id
      )
      const currentAccountantId = currentAssignment?.accountant?.id || currentAssignment?.accountantId

      // If accountant changed
      if (editClient.accountantId !== currentAccountantId) {
        // Remove old assignment if exists
        if (currentAccountantId) {
          try {
            await partnerAdminApi.clients.unassign(editingClient.id, currentAccountantId)
          } catch (err) {
            console.error('Error removing old assignment:', err)
          }
        }
        // Add new assignment if selected
        if (editClient.accountantId) {
          try {
            await partnerAdminApi.clients.assign(editingClient.id, editClient.accountantId)
          } catch (err) {
            console.error('Error adding new assignment:', err)
          }
        }
      }

      // Reset form and close modal
      setEditingClient(null)
      setEditClient({
        name: '',
        email: '',
        phone: '',
        cnpj: '',
        speciality: '',
        subscriptionPlan: 'basic',
        status: 'active',
        accountantId: ''
      })
      setShowEditModal(false)

      // Refresh clients list
      fetchClients()

    } catch (error: any) {
      console.error('Error updating client:', error)
      setError(error.response?.data?.message || error.message || 'Erro ao atualizar cliente')
    } finally {
      setSubmitting(false)
    }
  }

  // Open details modal
  const handleViewDetails = (client: Client) => {
    setSelectedClient(client)
    setShowDetailsModal(true)
  }

  // Open delete confirmation modal
  const handleOpenDeleteModal = (client: Client) => {
    setDeletingClient(client)
    setShowDeleteModal(true)
  }

  // Delete client
  const handleDeleteClient = async () => {
    if (!deletingClient) return

    try {
      setSubmitting(true)
      setError(null)

      // Call API to delete client
      // await partnerAdminApi.clients.delete(deletingClient.id)

      // For now, just remove from local state (API not implemented yet)
      setClients(prev => prev.filter(c => c.id !== deletingClient.id))

      setDeletingClient(null)
      setShowDeleteModal(false)

    } catch (error: any) {
      console.error('Error deleting client:', error)
      setError(error.response?.data?.message || error.message || 'Erro ao excluir cliente')
    } finally {
      setSubmitting(false)
    }
  }

  // Open cancel modal
  const handleOpenCancelModal = (client: Client) => {
    setCancellingClient(client)
    setCancellationReason('')
    setCancellationDetails('')
    setShowCancelModal(true)
  }

  // Cancel client with reason
  const handleCancelClient = async () => {
    if (!cancellingClient || !cancellationReason) return

    try {
      setSubmitting(true)
      setError(null)

      // Call API to cancel client
      await partnerAdminApi.clients.cancel(cancellingClient.id, {
        reason: cancellationReason,
        details: cancellationDetails || undefined
      })

      // Update local state
      setClients(prev => prev.map(c =>
        c.id === cancellingClient.id
          ? { ...c, status: 'cancelled' as const }
          : c
      ))

      setCancellingClient(null)
      setShowCancelModal(false)
      setCancellationReason('')
      setCancellationDetails('')

      // Refresh list to get updated data
      fetchClients()

    } catch (error: any) {
      console.error('Error cancelling client:', error)
      setError(error.response?.data?.message || error.message || 'Erro ao cancelar cliente')
    } finally {
      setSubmitting(false)
    }
  }

  // Reactivate cancelled client
  const handleReactivateClient = async (client: Client) => {
    try {
      setSubmitting(true)
      setError(null)

      await partnerAdminApi.clients.reactivate(client.id)

      // Refresh list
      fetchClients()

    } catch (error: any) {
      console.error('Error reactivating client:', error)
      setError(error.response?.data?.message || error.message || 'Erro ao reativar cliente')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    fetchClients()
  }, [searchTerm, filterStatus])

  // Fetch accountants for the dropdown
  const fetchAccountants = async () => {
    try {
      const response = await partnerAdminApi.accountants.list()
      setAccountants(response.accountants.map((acc: any) => ({
        id: acc.id,
        name: acc.name,
        email: acc.email
      })))
    } catch (err) {
      console.error('Error fetching accountants:', err)
    }
  }

  // Fetch client-accountant assignments for filtering
  const fetchAssignments = async () => {
    try {
      const response = await partnerAdminApi.accountants.getAllAssignments()
      const assignmentsMap = new Map<string, string>()
      response.assignments.forEach((a: any) => {
        const clientId = a.client?.id || a.clientId
        const accountantId = a.accountant?.id || a.accountantId
        if (clientId && accountantId) {
          assignmentsMap.set(clientId, accountantId)
        }
      })
      setClientAssignments(assignmentsMap)
    } catch (err) {
      console.error('Error fetching assignments:', err)
    }
  }

  useEffect(() => {
    fetchAccountants()
    fetchAssignments()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      case 'trial': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'basic': return 'bg-blue-100 text-blue-800'
      case 'pro': return 'bg-purple-100 text-purple-800'
      case 'enterprise': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.speciality.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus
    const matchesPlan = filterPlan === 'all' || client.plan === filterPlan

    // Filter by accountant
    let matchesAccountant = true
    if (filterAccountant !== 'all') {
      if (filterAccountant === 'unassigned') {
        matchesAccountant = !clientAssignments.has(client.id)
      } else {
        matchesAccountant = clientAssignments.get(client.id) === filterAccountant
      }
    }

    return matchesSearch && matchesStatus && matchesPlan && matchesAccountant
  })

  // Pagination calculations
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterStatus, filterPlan, filterAccountant])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="client-management">
      {/* Page Header */}
      <PageHeader
        title="Gestão de Clínicas"
        subtitle="Gerencie as clínicas e médicos parceiros"
        actions={
          <>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-secondary"
            >
              <Plus className="w-4 h-4" />
              <span className="btn-text">Cadastro Rápido</span>
            </button>
            <button
              onClick={() => setShowWizard(true)}
              className="btn-primary"
            >
              <Wand2 className="w-4 h-4" />
              <span className="btn-text">Assistente</span>
            </button>
          </>
        }
      />



      {/* Filters and Search */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: 'Todos Status' },
                { value: 'active', label: 'Ativo' },
                { value: 'suspended', label: 'Suspenso' },
                { value: 'trial', label: 'Trial' }
              ]}
            />

            <Select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              options={[
                { value: 'all', label: 'Todos Planos' },
                { value: 'basic', label: 'Básico' },
                { value: 'pro', label: 'Profissional' },
                { value: 'enterprise', label: 'Enterprise' }
              ]}
            />

            <Select
              value={filterAccountant}
              onChange={(e) => setFilterAccountant(e.target.value)}
              options={[
                { value: 'all', label: 'Todos Contadores' },
                { value: 'unassigned', label: 'Sem Contador' },
                ...accountants.map(acc => ({ value: acc.id, label: acc.name }))
              ]}
            />
          </div>
        </div>
      </div>

      {/* Clients List - Mobile Cards / Desktop Table */}
      <div className="clients-container">
        {/* Desktop Table */}
        <div className="table-wrapper">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Especialidade</th>
                <th>Contador</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Receita</th>
                <th>Último Acesso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="client-info">
                      <div className="client-name">{client.name}</div>
                      <div className="client-email">{client.email}</div>
                      {client.cnpj && (
                        <div className="client-cnpj">CNPJ: {client.cnpj}</div>
                      )}
                    </div>
                  </td>
                  <td>{client.speciality || <span className="text-gray-400 italic">-</span>}</td>
                  <td>
                    {(() => {
                      const accountantId = clientAssignments.get(client.id)
                      const accountant = accountantId ? accountants.find(a => a.id === accountantId) : null
                      return accountant ? (
                        <span className="text-sm text-gray-700">{accountant.name}</span>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )
                    })()}
                  </td>
                  <td>
                    <span className={`badge ${getPlanColor(client.plan)}`}>
                      {client.plan.charAt(0).toUpperCase() + client.plan.slice(1)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusColor(client.status)}`}>
                      {client.status === 'active' ? 'Ativo' : client.status === 'suspended' ? 'Suspenso' : 'Trial'}
                    </span>
                  </td>
                  <td>R$ {client.revenue.toLocaleString()}</td>
                  <td>{new Date(client.lastAccess).toLocaleDateString('pt-BR')}</td>
                  <td>
                    <div className="actions-cell">
                      <button
                        onClick={() => handleViewDetails(client)}
                        className="action-btn text-blue-600"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenEditModal(client)}
                        className="action-btn text-gray-600"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {client.status !== 'cancelled' ? (
                        <button
                          onClick={() => handleOpenCancelModal(client)}
                          className="action-btn text-orange-600"
                          title="Cancelar"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivateClient(client)}
                          className="action-btn text-green-600"
                          title="Reativar"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenDeleteModal(client)}
                        className="action-btn text-red-600"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          {paginatedClients.map((client) => (
            <div key={client.id} className="client-card">
              <div className="card-header">
                <div>
                  <h3 className="card-client-name">{client.name}</h3>
                  <p className="card-client-email">{client.email}</p>
                </div>
                <div className="card-badges">
                  <span className={`badge ${getStatusColor(client.status)}`}>
                    {client.status === 'active' ? 'Ativo' : client.status === 'suspended' ? 'Suspenso' : 'Trial'}
                  </span>
                </div>
              </div>
              <div className="card-body">
                <div className="card-row">
                  <span className="card-label">Especialidade</span>
                  <span className="card-value">{client.speciality}</span>
                </div>
                <div className="card-row">
                  <span className="card-label">Contador</span>
                  {(() => {
                    const accountantId = clientAssignments.get(client.id)
                    const accountant = accountantId ? accountants.find(a => a.id === accountantId) : null
                    return accountant ? (
                      <span className="card-value">{accountant.name}</span>
                    ) : (
                      <span className="card-value text-gray-400 italic">-</span>
                    )
                  })()}
                </div>
                <div className="card-row">
                  <span className="card-label">Plano</span>
                  <span className={`badge ${getPlanColor(client.plan)}`}>
                    {client.plan.charAt(0).toUpperCase() + client.plan.slice(1)}
                  </span>
                </div>
                <div className="card-row">
                  <span className="card-label">Receita</span>
                  <span className="card-value font-semibold">R$ {client.revenue.toLocaleString()}</span>
                </div>
                <div className="card-row">
                  <span className="card-label">Último Acesso</span>
                  <span className="card-value">{new Date(client.lastAccess).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
              <div className="card-actions">
                <button className="card-action-btn" title="Ver detalhes">
                  <Eye className="w-4 h-4" />
                  <span>Ver</span>
                </button>
                <button onClick={() => handleOpenEditModal(client)} className="card-action-btn" title="Editar">
                  <Edit className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button className="card-action-btn text-red-600" title="Excluir">
                  <Trash2 className="w-4 h-4" />
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredClients.length)} de {filteredClients.length}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="pagination-pages">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {filteredClients.length === 0 && (
        <div className="empty-state">
          <Building className="empty-icon" />
          <h3 className="empty-title">Nenhuma clínica encontrada</h3>
          <p className="empty-text">
            {clients.length === 0
              ? 'Adicione sua primeira clínica clicando no botão "Nova Clínica".'
              : 'Tente ajustar os filtros ou adicionar novas clínicas.'}
          </p>
          {clients.length === 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary mt-4"
            >
              <Plus className="w-4 h-4" />
              Adicionar Primeira Clínica
            </button>
          )}
        </div>
      )}

      {/* Client Onboarding Wizard */}
      <ClientOnboardingWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={() => {
          fetchClients()
        }}
      />

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Nova Clínica</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="error-alert">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Dr. João Silva"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="joao@clinica.com"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">CNPJ</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    value={newClient.cnpj}
                    onChange={(e) => handleCnpjChange(e.target.value)}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                    className={`form-input ${
                      cnpjStatus === 'valid'
                        ? 'border-green-500 focus:ring-green-500'
                        : cnpjStatus === 'invalid' || cnpjStatus === 'exists'
                        ? 'border-red-500 focus:ring-red-500'
                        : ''
                    }`}
                  />
                  <div className="input-icon">
                    {cnpjStatus === 'validating' && (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    )}
                    {cnpjStatus === 'valid' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {(cnpjStatus === 'invalid' || cnpjStatus === 'exists') && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                {cnpjStatus === 'exists' && cnpjValidation.existsCheck?.client && (
                  <p className="input-error">
                    CNPJ já cadastrado para: {cnpjValidation.existsCheck.client.name}
                  </p>
                )}
                {cnpjStatus === 'invalid' && (
                  <p className="input-error">
                    {cnpjValidation.fullValidation?.errors?.[0] || cnpjValidation.error || 'CNPJ inválido'}
                  </p>
                )}
                {cnpjStatus === 'valid' && cnpjValidation.fullValidation && (
                  <div className="input-success">
                    <p>{cnpjValidation.fullValidation.rfData?.razaoSocial}</p>
                    {cnpjValidation.fullValidation.warnings?.length > 0 && (
                      <p className="text-yellow-600">{cnpjValidation.fullValidation.warnings[0]}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Especialidade</label>
                <input
                  type="text"
                  value={newClient.speciality}
                  onChange={(e) => setNewClient({ ...newClient, speciality: e.target.value })}
                  placeholder="Cardiologia"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Plano</label>
                <Select
                  value={newClient.subscriptionPlan}
                  onChange={(e) => setNewClient({ ...newClient, subscriptionPlan: e.target.value })}
                  options={[
                    { value: 'basic', label: 'Básico' },
                    { value: 'pro', label: 'Profissional' },
                    { value: 'enterprise', label: 'Enterprise' }
                  ]}
                  fullWidth
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contador Responsável</label>
                <Select
                  value={newClient.accountantId}
                  onChange={(e) => setNewClient({ ...newClient, accountantId: e.target.value })}
                  options={[
                    { value: '', label: 'Selecione um contador...' },
                    ...accountants.map(acc => ({ value: acc.id, label: `${acc.name} (${acc.email})` }))
                  ]}
                  fullWidth
                />
                <p className="text-xs text-gray-500 mt-1">
                  O contador selecionado será responsável por gerenciar este cliente
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddClient}
                disabled={submitting || !newClient.name || !newClient.email}
                className="btn-primary"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Cadastrar Clínica
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Editar Clínica</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingClient(null)
                  setError(null)
                }}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="error-alert">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={editClient.name}
                  onChange={(e) => setEditClient({ ...editClient, name: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={editClient.email}
                  onChange={(e) => setEditClient({ ...editClient, email: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="tel"
                  value={editClient.phone}
                  onChange={(e) => setEditClient({ ...editClient, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">CNPJ</label>
                <input
                  type="text"
                  value={editClient.cnpj}
                  disabled
                  className="form-input bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="input-hint">CNPJ não pode ser alterado</p>
              </div>

              <div className="form-group">
                <label className="form-label">Especialidade</label>
                <input
                  type="text"
                  value={editClient.speciality}
                  onChange={(e) => setEditClient({ ...editClient, speciality: e.target.value })}
                  placeholder="Cardiologia"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Plano</label>
                <Select
                  value={editClient.subscriptionPlan}
                  onChange={(e) => setEditClient({ ...editClient, subscriptionPlan: e.target.value })}
                  options={[
                    { value: 'basic', label: 'Básico' },
                    { value: 'pro', label: 'Profissional' },
                    { value: 'enterprise', label: 'Enterprise' }
                  ]}
                  fullWidth
                />
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <Select
                  value={editClient.status}
                  onChange={(e) => setEditClient({ ...editClient, status: e.target.value as 'active' | 'suspended' | 'trial' })}
                  options={[
                    { value: 'active', label: 'Ativo' },
                    { value: 'trial', label: 'Trial' },
                    { value: 'suspended', label: 'Suspenso' }
                  ]}
                  fullWidth
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contador Responsável</label>
                <Select
                  value={editClient.accountantId}
                  onChange={(e) => setEditClient({ ...editClient, accountantId: e.target.value })}
                  options={[
                    { value: '', label: 'Sem contador atribuído' },
                    ...accountants.map(acc => ({ value: acc.id, label: `${acc.name} (${acc.email})` }))
                  ]}
                  fullWidth
                />
                <p className="text-xs text-gray-500 mt-1">
                  Altere o contador responsável por este cliente
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingClient(null)
                  setError(null)
                }}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateClient}
                disabled={submitting || !editClient.name || !editClient.email}
                className="btn-primary"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedClient && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Detalhes da Clínica</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedClient(null)
                }}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-6">
                {/* Client Header */}
                <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedClient.name}</h3>
                    <p className="text-gray-500">{selectedClient.email}</p>
                  </div>
                </div>

                {/* Status and Plan */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Status</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedClient.status)}`}>
                      {selectedClient.status === 'active' ? 'Ativo' : selectedClient.status === 'trial' ? 'Trial' : 'Suspenso'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Plano</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanColor(selectedClient.plan)}`}>
                      {selectedClient.plan === 'basic' ? 'Básico' : selectedClient.plan === 'pro' ? 'Profissional' : 'Enterprise'}
                    </span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Especialidade</p>
                    <p className="font-medium text-gray-900">{selectedClient.speciality || <span className="text-gray-400 italic">-</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Receita Mensal</p>
                    <p className="font-medium text-gray-900">R$ {selectedClient.revenue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Último Acesso</p>
                    <p className="font-medium text-gray-900">{new Date(selectedClient.lastAccess).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Documentos</p>
                    <p className="font-medium text-gray-900">{selectedClient.documentsCount} pendentes</p>
                  </div>
                  {selectedClient.cnpj && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">CNPJ</p>
                      <p className="font-medium text-gray-900">{selectedClient.cnpj}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedClient(null)
                }}
                className="btn-cancel"
              >
                Fechar
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  handleOpenEditModal(selectedClient)
                }}
                className="btn-primary"
              >
                <Edit className="w-4 h-4" />
                Editar Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingClient && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2 className="modal-title text-red-600">Excluir Clínica</h2>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletingClient(null)
                }}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Tem certeza que deseja excluir?
                </h3>
                <p className="text-gray-500 mb-4">
                  O cliente <strong>{deletingClient.name}</strong> será removido permanentemente. Esta ação não pode ser desfeita.
                </p>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer justify-center gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletingClient(null)
                  setError(null)
                }}
                className="btn-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteClient}
                disabled={submitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Sim, Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Client Modal */}
      {showCancelModal && cancellingClient && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title text-orange-600">Cancelar Clínica</h2>
              <button
                onClick={() => {
                  setShowCancelModal(false)
                  setCancellingClient(null)
                  setCancellationReason('')
                  setCancellationDetails('')
                }}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              <div className="py-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserX className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                  Cancelar assinatura de {cancellingClient.name}?
                </h3>
                <p className="text-gray-500 mb-6 text-center text-sm">
                  O cliente será marcado como cancelado. Você poderá reativá-lo posteriormente se necessário.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo do cancelamento <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Selecione um motivo...</option>
                      <option value="Preço">Preço</option>
                      <option value="Mudança de Contador">Mudança de Contador</option>
                      <option value="Fechamento">Fechamento da Clínica</option>
                      <option value="Insatisfação">Insatisfação com o Serviço</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detalhes adicionais (opcional)
                    </label>
                    <textarea
                      value={cancellationDetails}
                      onChange={(e) => setCancellationDetails(e.target.value)}
                      placeholder="Adicione informações relevantes sobre o cancelamento..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false)
                  setCancellingClient(null)
                  setCancellationReason('')
                  setCancellationDetails('')
                  setError(null)
                }}
                className="btn-cancel"
              >
                Voltar
              </button>
              <button
                onClick={handleCancelClient}
                disabled={submitting || !cancellationReason}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Cancelando...
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4" />
                    Confirmar Cancelamento
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Show credentials after clinic creation */}
      {showSuccessModal && createdCredentials && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title text-green-600">Clínica Criada com Sucesso!</h2>
              <button
                onClick={() => {
                  setShowSuccessModal(false)
                  setCreatedCredentials(null)
                }}
                className="modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body">
              <div className="py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 text-center">
                  {createdCredentials.clinicName}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6 text-center text-sm">
                  A clínica foi criada e um usuário administrador foi configurado. Compartilhe as credenciais abaixo com o responsável pela clínica.
                </p>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Email de Acesso
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white dark:bg-gray-700 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-sm font-mono text-gray-900 dark:text-gray-100">
                        {createdCredentials.email}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(createdCredentials.email)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Copiar email"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Senha Temporária
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white dark:bg-gray-700 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-sm font-mono text-gray-900 dark:text-gray-100">
                        {createdCredentials.password}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(createdCredentials.password)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Copiar senha"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    <strong>Importante:</strong> O usuário deverá alterar a senha no primeiro acesso. Guarde essas informações em local seguro.
                  </p>
                </div>
              </div>
            </div>

            <div className="modal-footer justify-center gap-3">
              <button
                onClick={() => {
                  const text = `Clínica: ${createdCredentials.clinicName}\nEmail: ${createdCredentials.email}\nSenha: ${createdCredentials.password}`
                  navigator.clipboard.writeText(text)
                }}
                className="btn-secondary"
              >
                Copiar Tudo
              </button>
              <button
                onClick={() => {
                  setShowSuccessModal(false)
                  setCreatedCredentials(null)
                }}
                className="btn-primary"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .client-management {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Page Header */
        .page-header {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 640px) {
          .page-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }
        }

        .header-text {
          flex: 1;
        }

        .page-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        :root.dark .page-title {
          color: #f9fafb;
        }

        @media (min-width: 640px) {
          .page-title {
            font-size: 1.5rem;
          }
        }

        .page-subtitle {
          color: #6b7280;
          margin-top: 0.25rem;
          font-size: 0.875rem;
        }

        :root.dark .page-subtitle {
          color: #9ca3af;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          width: 100%;
        }

        @media (min-width: 640px) {
          .header-actions {
            width: auto;
          }
        }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #2563eb;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        @media (min-width: 640px) {
          .btn-primary {
            flex: none;
          }
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #f3f4f6;
          color: #374151;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        :root.dark .btn-secondary {
          background: #374151;
          color: #f3f4f6;
          border-color: #4b5563;
        }

        @media (min-width: 640px) {
          .btn-secondary {
            flex: none;
          }
        }

        .btn-secondary:hover {
          background: #e5e7eb;
        }

        :root.dark .btn-secondary:hover {
          background: #4b5563;
        }

        .btn-text {
          display: none;
        }

        @media (min-width: 480px) {
          .btn-text {
            display: inline;
          }
        }

        .btn-cancel {
          padding: 0.5rem 1rem;
          color: #374151;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          border: none;
        }

        .btn-cancel:hover {
          color: #111827;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        @media (min-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
          }
        }

        .stat-card {
          background: white;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        @media (min-width: 640px) {
          .stat-card {
            padding: 1.5rem;
          }
        }

        .stat-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .stat-info {
          min-width: 0;
        }

        .stat-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
          margin: 0;
        }

        @media (min-width: 640px) {
          .stat-label {
            font-size: 0.875rem;
          }
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          margin-top: 0.25rem;
        }

        @media (min-width: 640px) {
          .stat-value {
            font-size: 1.5rem;
          }
        }

        .stat-icon {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }

        @media (min-width: 640px) {
          .stat-icon {
            width: 32px;
            height: 32px;
          }
        }

        /* Filters */
        .filters-card {
          background: white;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        :root.dark .filters-card {
          background: #1f2937;
          border-color: #374151;
        }

        @media (min-width: 640px) {
          .filters-card {
            padding: 1.5rem;
          }
        }

        .filters-row {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .filters-row {
            flex-direction: row;
            gap: 1rem;
          }
        }

        .search-container {
          position: relative;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.5rem 0.75rem 0.5rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #111827;
        }

        :root.dark .search-input {
          background: #374151;
          border-color: #4b5563;
          color: #f9fafb;
        }

        :root.dark .search-input::placeholder {
          color: #9ca3af;
        }

        .search-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .filters-group {
          display: flex;
          gap: 0.5rem;
        }

        .filter-select {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          min-width: 0;
        }

        @media (min-width: 640px) {
          .filter-select {
            flex: none;
            width: auto;
          }
        }

        .filter-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        /* Clients Container */
        .clients-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        :root.dark .clients-container {
          background: #1f2937;
          border-color: #374151;
        }

        /* Desktop Table */
        .table-wrapper {
          display: none;
          overflow-x: auto;
        }

        @media (min-width: 768px) {
          .table-wrapper {
            display: block;
          }
        }

        .clients-table {
          width: 100%;
          border-collapse: collapse;
        }

        .clients-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #6b7280;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }

        :root.dark .clients-table th {
          background: #111827;
          color: #9ca3af;
          border-bottom-color: #374151;
        }

        .clients-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          font-size: 0.875rem;
          color: #111827;
        }

        :root.dark .clients-table td {
          color: #f3f4f6;
          border-bottom-color: #374151;
        }

        .clients-table tbody tr:hover {
          background: #f9fafb;
        }

        :root.dark .clients-table tbody tr:hover {
          background: #374151;
        }

        .client-info {
          min-width: 200px;
        }

        .client-name {
          font-weight: 500;
          color: #111827;
        }

        :root.dark .client-name {
          color: #f9fafb;
        }

        .client-email {
          font-size: 0.875rem;
          color: #6b7280;
        }

        :root.dark .client-email {
          color: #9ca3af;
        }

        .client-cnpj {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.625rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .actions-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.25rem;
          border: none;
          background: transparent;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .action-btn:hover {
          background: #f3f4f6;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        @media (min-width: 768px) {
          .mobile-cards {
            display: none;
          }
        }

        .client-card {
          border-bottom: 1px solid #e5e7eb;
          padding: 1rem;
        }

        :root.dark .client-card {
          border-bottom-color: #374151;
        }

        .client-card:last-child {
          border-bottom: none;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }

        .card-client-name {
          font-weight: 600;
          color: #111827;
          margin: 0;
          font-size: 0.9375rem;
        }

        :root.dark .card-client-name {
          color: #f9fafb;
        }

        .card-client-email {
          font-size: 0.8125rem;
          color: #6b7280;
          margin: 0;
          margin-top: 0.125rem;
        }

        :root.dark .card-client-email {
          color: #9ca3af;
        }

        .card-badges {
          flex-shrink: 0;
        }

        .card-body {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8125rem;
        }

        .card-label {
          color: #6b7280;
        }

        :root.dark .card-label {
          color: #9ca3af;
        }

        .card-value {
          color: #111827;
        }

        :root.dark .card-value {
          color: #f3f4f6;
        }

        .card-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #f3f4f6;
        }

        :root.dark .card-actions {
          border-top-color: #374151;
        }

        .card-action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.5rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.75rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        :root.dark .card-action-btn {
          background: #374151;
          border-color: #4b5563;
          color: #d1d5db;
        }

        .card-action-btn:hover {
          background: #f3f4f6;
        }

        :root.dark .card-action-btn:hover {
          background: #4b5563;
        }

        .card-action-btn.text-red-600 {
          color: #dc2626;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 3rem 1.5rem;
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        :root.dark .empty-state {
          background: #1f2937;
          border-color: #374151;
        }

        .empty-icon {
          width: 48px;
          height: 48px;
          color: #9ca3af;
          margin: 0 auto;
        }

        :root.dark .empty-icon {
          color: #6b7280;
        }

        .empty-title {
          margin-top: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #111827;
        }

        :root.dark .empty-title {
          color: #f9fafb;
        }

        .empty-text {
          margin-top: 0.25rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        :root.dark .empty-text {
          color: #9ca3af;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 50;
        }

        @media (max-width: 480px) {
          .modal-overlay {
            align-items: flex-end;
            padding: 0;
          }
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          width: 100%;
          max-width: 448px;
          max-height: calc(100vh - 2rem);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        :root.dark .modal-content {
          background: #1f2937;
          border: 1px solid #374151;
        }

        @media (max-width: 480px) {
          .modal-content {
            border-radius: 12px 12px 0 0;
            max-height: 90vh;
          }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        :root.dark .modal-header {
          border-bottom-color: #374151;
        }

        .modal-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        :root.dark .modal-title {
          color: #f9fafb;
        }

        .modal-close {
          padding: 0.25rem;
          color: #9ca3af;
          cursor: pointer;
          background: transparent;
          border: none;
          border-radius: 4px;
        }

        .modal-close:hover {
          color: #6b7280;
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .modal-footer {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        :root.dark .modal-footer {
          border-top-color: #374151;
        }

        /* Form */
        .form-group {
          margin-bottom: 1rem;
        }

        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        :root.dark .form-label {
          color: #d1d5db;
        }

        .form-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          background: white;
          color: #111827;
        }

        :root.dark .form-input {
          background: #374151;
          border-color: #4b5563;
          color: #f9fafb;
        }

        :root.dark .form-input::placeholder {
          color: #9ca3af;
        }

        .form-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .input-with-icon {
          position: relative;
        }

        .input-with-icon .form-input {
          padding-right: 2.5rem;
        }

        .input-icon {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
        }

        .input-error {
          margin-top: 0.25rem;
          font-size: 0.875rem;
          color: #dc2626;
        }

        .input-success {
          margin-top: 0.25rem;
          font-size: 0.875rem;
          color: #16a34a;
        }

        .input-hint {
          margin-top: 0.25rem;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .error-alert {
          background: #fef2f2;
          color: #dc2626;
          padding: 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        /* Pagination */
        .pagination {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        :root.dark .pagination {
          border-top-color: #374151;
        }

        @media (min-width: 640px) {
          .pagination {
            flex-direction: row;
            justify-content: space-between;
          }
        }

        .pagination-info {
          font-size: 0.875rem;
          color: #6b7280;
        }

        :root.dark .pagination-info {
          color: #9ca3af;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        :root.dark .pagination-btn {
          background: #374151;
          border-color: #4b5563;
          color: #d1d5db;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        :root.dark .pagination-btn:hover:not(:disabled) {
          background: #4b5563;
          border-color: #6b7280;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-pages {
          font-size: 0.875rem;
          color: #374151;
          padding: 0 0.5rem;
        }

        :root.dark .pagination-pages {
          color: #d1d5db;
        }

        /* Touch friendly */
        @media (pointer: coarse) {
          .form-input,
          .filter-select,
          .search-input {
            min-height: 44px;
            font-size: 16px;
          }

          .btn-primary,
          .btn-secondary {
            min-height: 44px;
          }

          .card-action-btn {
            min-height: 40px;
          }
        }
      `}</style>
    </div>
  )
}
